import { ref } from 'vue';
import store from '@/store';
import { config } from '@/config';
import { useToast } from '@/composables/common/useToast';

/**
 * Composable que dispara la generación del excel de asientos del recinto
 * (acción back.lista.exportar.excel-asientos).
 *
 * Encapsula el dispatch, el guard de submit y la notificación de error.
 * El toast de éxito se gestiona en el caller (modal) porque vive fuera
 * del composable para evitar duplicados al cerrar la modal.
 *
 * @returns {{
 *     loading: import('vue').Ref<boolean>,
 *     result: import('vue').Ref<{ ok: true } | { ok: false, error: any } | null>,
 *     generar: (listaId?: number|string) => Promise<{ ok: true } | { ok: false, error: any }>,
 * }}
 */
export function useGenerarExcelAsientos() {
    const toast = useToast();

    const loading = ref(false);
    const result = ref(null);

    /**
     * Dispara la generación del excel.
     *
     * Si no se pasa `listaId`, se lee del getter `getListaAbonados`.
     * Si ya hay una petición en vuelo, retorna `{ ok: false, error: 'in-flight' }`
     * sin dispatchear de nuevo.
     *
     * @param {number|string} [listaId]
     */
    async function generar(listaId) {
        if (loading.value) {
            return { ok: false, error: 'in-flight' };
        }

        const targetId = listaId ?? store.getters.getListaAbonados?.id;

        loading.value = true;
        try {
            await store.dispatch(
                config.getVuexAlias('back.lista.exportar.excel-asientos'),
                { filters: {}, lista_id: targetId }
            );
            result.value = { ok: true };
            return { ok: true };
        } catch (error) {
            toast.error('Ha ocurrido un error al generar el excel.');
            result.value = { ok: false, error };
            return { ok: false, error };
        } finally {
            loading.value = false;
        }
    }

    return {
        loading,
        result,
        generar,
    };
}